import React, { useContext, useEffect, useMemo, useState } from 'react'
import { styled } from 'linaria/react'
import { cx } from 'linaria'
import { useIntl } from '@jetshop/intl'
import { SiteContext } from '../../Global/SiteContext'
import { DIGIFI_FILTER_FIELD_NAMES } from '../../DigifiSearch/constants'

const SLIDER_STEPS = 1000

const PRICE_PRESETS = [
   [0, 500],
   [500, 2000],
   [2000, 5000],
   [5000, null],
]

const PriceContentWrapper = styled.div`
   .price-slider {
      position: relative;
      height: 24px;
      margin: 0 11px;
   }

   .price-slider-track,
   .price-slider-range {
      position: absolute;
      top: 50%;
      height: 4px;
      transform: translateY(-50%);
      border-radius: 2px;
   }

   .price-slider-track {
      left: 0;
      right: 0;
      background-color: #dfe2ee;
   }

   .price-slider-range {
      background-color: var(--color-vb-blue);
   }

   .price-slider input[type='range'] {
      position: absolute;
      left: -11px;
      width: calc(100% + 22px);
      top: 0;
      height: 24px;
      margin: 0;
      background: none;
      pointer-events: none;
      -webkit-appearance: none;
      appearance: none;

      &:focus {
         outline: none;
      }

      &::-webkit-slider-thumb {
         -webkit-appearance: none;
         pointer-events: auto;
         width: 22px;
         height: 22px;
         border-radius: 50%;
         background-color: var(--color-white);
         border: 3px solid var(--color-vb-blue-darker);
         cursor: pointer;
      }

      &::-moz-range-thumb {
         pointer-events: auto;
         width: 16px;
         height: 16px;
         border-radius: 50%;
         background-color: var(--color-white);
         border: 3px solid var(--color-vb-blue-darker);
         cursor: pointer;
      }

      &:focus-visible::-webkit-slider-thumb {
         box-shadow: 0 0 0 3px rgba(25, 81, 120, 0.3);
      }
   }

   .price-slider-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 0.4rem;
      font-size: 0.8rem;
      color: #707070;
   }

   .price-inputs {
      display: flex;
      align-items: flex-end;
      gap: 0.5rem;
      margin-top: 1.25rem;
   }

   .price-input-group {
      flex: 1;
      min-width: 0;
   }

   .price-input-label {
      display: block;
      margin-bottom: 0.35rem;
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: #707070;
   }

   .price-input-wrapper {
      position: relative;
   }

   .price-input {
      width: 100%;
      padding: 10px 36px 10px 12px;
      border: 1px solid var(--color-separator);
      border-radius: 999px;
      font-family: var(--font-industry);
      font-weight: 700;
      font-size: 1rem;
      text-align: center;
      -moz-appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
         -webkit-appearance: none;
         margin: 0;
      }

      &:focus {
         outline: none;
         border-color: var(--color-vb-blue);
      }
   }

   .price-input-currency {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: #707070;
      pointer-events: none;
   }

   .price-input-separator {
      padding-bottom: 10px;
      color: #707070;
   }

   .price-presets {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin: 1.25rem 0;
   }

   .price-preset {
      padding: 6px 14px;
      background-color: var(--color-white);
      border: 1px solid var(--color-separator);
      border-radius: 999px;
      font-weight: 600;
      font-size: 0.9rem;
      cursor: pointer;

      &:hover {
         border-color: var(--color-vb-blue);
      }

      &.is-selected {
         background-color: var(--color-vb-blue);
         border-color: var(--color-vb-blue);
         color: var(--color-white);
      }
   }
`

export const formatNumber = value => Math.round(value).toLocaleString('sv-SE')
export const formatKr = value => `${formatNumber(value)} kr`

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// Rounds to a step that fits the price level, so slider values stay readable
const roundPrice = value => {
   if (value < 1000) return Math.round(value / 10) * 10
   if (value < 10000) return Math.round(value / 100) * 100
   return Math.round(value / 500) * 500
}

/**
 * Price bounds of the current listing without the price filter applied, in the active VAT mode.
 */
export const usePriceBounds = facets => {
   const { showVat } = useContext(SiteContext)
   return useMemo(() => {
      const stats = showVat
         ? facets?.priceFacets?.defaultPriceIncVat
         : facets?.priceFacets?.defaultPriceExVat
      return { min: stats?.min ?? 0, max: stats?.max ?? 0 }
   }, [facets?.priceFacets, showVat])
}

export const getSelectedPriceRange = filters => {
   const value = filters?.find(
      f => f?.fieldName === DIGIFI_FILTER_FIELD_NAMES.PRICE
   )?.value
   return Array.isArray(value) && value.length === 2 ? value : null
}

/**
 * Price range slider, manual inputs and quick price ranges. Used in the desktop dropdown and the mobile drawer.
 * The slider uses a squared scale so the low price range, where most products are, gets more room.
 */
const PriceFilterContent = ({ filters, facets, updateFilters, className }) => {
   const t = useIntl()
   const bounds = usePriceBounds(facets)

   const selectedRange = getSelectedPriceRange(filters)
   const hasSelection = !!selectedRange

   const [draft, setDraft] = useState({ min: bounds.min, max: bounds.max })
   const [inputs, setInputs] = useState({ min: '', max: '' })
   // Slider handle positions are kept separately, rounding a small step to a price can map back to the same position
   const [sliderPos, setSliderPos] = useState({ min: 0, max: SLIDER_STEPS })

   const selectedMin = hasSelection ? Number(selectedRange[0]) : null
   const selectedMax = hasSelection ? Number(selectedRange[1]) : null

   const span = Math.max(bounds.max - bounds.min, 1)
   const toPosition = value =>
      Math.round(
         Math.sqrt(clamp((value - bounds.min) / span, 0, 1)) * SLIDER_STEPS
      )
   const fromPosition = position => {
      if (position >= SLIDER_STEPS) return bounds.max
      if (position <= 0) return bounds.min
      return clamp(
         roundPrice(bounds.min + Math.pow(position / SLIDER_STEPS, 2) * span),
         bounds.min,
         bounds.max
      )
   }

   const syncDraft = next => {
      setDraft(next)
      setInputs({ min: Math.round(next.min), max: Math.round(next.max) })
      setSliderPos({ min: toPosition(next.min), max: toPosition(next.max) })
   }

   useEffect(() => {
      const next =
         selectedMin !== null
            ? {
                 min: clamp(selectedMin, bounds.min, bounds.max),
                 max: clamp(selectedMax, bounds.min, bounds.max),
              }
            : { min: bounds.min, max: bounds.max }
      syncDraft(next)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [bounds.min, bounds.max, selectedMin, selectedMax])

   const clearPrice = () => {
      updateFilters?.([
         { fieldName: DIGIFI_FILTER_FIELD_NAMES.PRICE, value: null },
      ])
   }

   const applyRange = (min, max) => {
      if (min >= max) return
      // The full range is the same as no price filter
      if (min <= bounds.min && max >= bounds.max) {
         if (hasSelection) clearPrice()
         return
      }
      if (hasSelection && min === selectedMin && max === selectedMax) return
      updateFilters?.([
         {
            fieldName: DIGIFI_FILTER_FIELD_NAMES.PRICE,
            value: [Math.round(min), Math.round(max)],
         },
      ])
   }

   const handleSliderChange = (isMin, position) => {
      const nextPos = isMin
         ? { ...sliderPos, min: Math.min(Number(position), sliderPos.max) }
         : { ...sliderPos, max: Math.max(Number(position), sliderPos.min) }
      const next = {
         min: fromPosition(nextPos.min),
         max: fromPosition(nextPos.max),
      }
      setSliderPos(nextPos)
      setDraft(next)
      setInputs({ min: Math.round(next.min), max: Math.round(next.max) })
   }

   // Reads the blurred field from the event, the state can lag behind a fast type-and-tab
   const commitInputs = (field, value) => {
      const values = { ...inputs, [field]: value }
      const min = clamp(
         Number(values.min) || bounds.min,
         bounds.min,
         bounds.max
      )
      const max = clamp(
         Number(values.max) || bounds.max,
         bounds.min,
         bounds.max
      )
      const next = { min: Math.min(min, max), max: Math.max(min, max) }
      syncDraft(next)
      applyRange(next.min, next.max)
   }

   const getPresetLabel = ([min, max]) => {
      if (!min) return t('Under { price }', { price: formatKr(max) })
      if (max === null) return t('Over { price }', { price: formatKr(min) })
      return `${formatNumber(min)}-${formatKr(max)}`
   }

   const presets = PRICE_PRESETS.map(preset => ({
      label: getPresetLabel(preset),
      range: [
         Math.max(preset[0], bounds.min),
         preset[1] === null ? bounds.max : Math.min(preset[1], bounds.max),
      ],
   })).filter(preset => preset.range[0] < preset.range[1])

   if (!bounds.max) {
      return null
   }

   const startPercent = (sliderPos.min / SLIDER_STEPS) * 100
   const endPercent = (sliderPos.max / SLIDER_STEPS) * 100

   return (
      <PriceContentWrapper className={className}>
         <div className="price-slider">
            <div className="price-slider-track" />
            <div
               className="price-slider-range"
               style={{
                  left: `${startPercent}%`,
                  width: `${endPercent - startPercent}%`,
               }}
            />
            <input
               type="range"
               min={0}
               max={SLIDER_STEPS}
               value={sliderPos.min}
               aria-label={t('Lowest price')}
               onChange={e => handleSliderChange(true, e.target.value)}
               onPointerUp={() => applyRange(draft.min, draft.max)}
               onKeyUp={() => applyRange(draft.min, draft.max)}
            />
            <input
               type="range"
               min={0}
               max={SLIDER_STEPS}
               value={sliderPos.max}
               aria-label={t('Highest price')}
               onChange={e => handleSliderChange(false, e.target.value)}
               onPointerUp={() => applyRange(draft.min, draft.max)}
               onKeyUp={() => applyRange(draft.min, draft.max)}
            />
         </div>
         <div className="price-slider-labels">
            <span>{formatKr(bounds.min)}</span>
            <span>{formatKr(bounds.max)}</span>
         </div>

         <div className="price-inputs">
            <label className="price-input-group">
               <span className="price-input-label">{t('From')}</span>
               <span className="price-input-wrapper">
                  <input
                     type="number"
                     className="price-input"
                     value={inputs.min}
                     onChange={e =>
                        setInputs({ ...inputs, min: e.target.value })
                     }
                     onBlur={e => commitInputs('min', e.target.value)}
                     onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                     onFocus={e => e.target.select()}
                  />
                  <span className="price-input-currency">kr</span>
               </span>
            </label>
            <span className="price-input-separator">–</span>
            <label className="price-input-group">
               <span className="price-input-label">{t('To')}</span>
               <span className="price-input-wrapper">
                  <input
                     type="number"
                     className="price-input"
                     value={inputs.max}
                     onChange={e =>
                        setInputs({ ...inputs, max: e.target.value })
                     }
                     onBlur={e => commitInputs('max', e.target.value)}
                     onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                     onFocus={e => e.target.select()}
                  />
                  <span className="price-input-currency">kr</span>
               </span>
            </label>
         </div>

         {presets.length > 0 && (
            <div className="price-presets">
               {presets.map(preset => {
                  const isSelected =
                     hasSelection &&
                     Math.round(draft.min) === Math.round(preset.range[0]) &&
                     Math.round(draft.max) === Math.round(preset.range[1])
                  return (
                     <button
                        key={preset.label}
                        type="button"
                        className={cx(
                           'price-preset',
                           isSelected && 'is-selected'
                        )}
                        aria-pressed={isSelected}
                        onClick={() =>
                           applyRange(preset.range[0], preset.range[1])
                        }
                     >
                        {preset.label}
                     </button>
                  )
               })}
            </div>
         )}
      </PriceContentWrapper>
   )
}

export default PriceFilterContent
